import clsx from 'clsx';
import styles from './Button.module.scss';
import React from 'react';
import { Trans } from 'react-i18next';
import { TranslationKey } from '@/types/i18n.types';
import type { IconComponent } from '@/types/icon.types';

export enum EButtonSize {
  xs = 'xs',
  xsRounded = 'xsRounded',
  sm = 'sm',
  md = 'md',
  lg = 'lg',
  xl = 'xl',
}

// Figma: Hierarchy — the semantic color of the button
export enum EButtonHierarchy {
  primary = 'primary',
  success = 'success',
  error = 'error',
  warning = 'warning',
  // Figma now labels this hierarchy "Pending" — same purple tokens as before
  focus = 'focus',
  upsell = 'upsell',
  inverse = 'inverse',
  // Gray icon-only actions such as close, back and menu; only the ghost style is defined
  neutral = 'neutral',
}

// Figma: Style — the visual treatment applied on top of the hierarchy
export enum EButtonStyle {
  solid = 'style-solid',
  tonal = 'style-tonal',
  outlined = 'style-outlined',
  ghost = 'style-ghost',
  // Only the Upsell hierarchy has a Style=Inverse variant in Figma
  inverse = 'style-inverse',
}

export interface IButtonProps {
  children?: React.ReactNode;
  onClick?: () => void;
  size: EButtonSize;
  hierarchy: EButtonHierarchy;
  buttonStyle?: EButtonStyle;
  text?: TranslationKey;
  iconLead?: IconComponent;
  iconTrail?: IconComponent;
  iconStyle?: string;
  disabled?: boolean;
  isLoading?: boolean;
  fillWidth?: boolean;
  isSquare?: boolean;
  isRounded?: boolean;
  className?: string;
  dataAttribute?: string;
  ariaLabel?: string;
  ariaPressed?: boolean;
  ariaExpanded?: boolean;
  ariaHasPopup?: React.AriaAttributes['aria-haspopup'];
}

export const Button: React.FC<IButtonProps> = (props: IButtonProps) => {
  const IconLead = props.iconLead;
  const IconTrail = props.iconTrail;
  const isIconOnly =
    props.isSquare ||
    (!props.text && !props.children && (IconLead || IconTrail));
  const isDisabled = props.disabled || props.isLoading;

  const buttonStyle = props.buttonStyle ?? EButtonStyle.solid;
  const iconClass = clsx(styles.icon, props.iconStyle);

  const buttonClass = clsx(
    styles.button,
    styles[props.size],
    styles[props.hierarchy],
    styles[buttonStyle],
    props.fillWidth && styles.fillWidth,
    isDisabled && styles.disabled,
    isIconOnly && styles.iconOnly,
    props.isRounded && styles.rounded,
    props.className
  );

  return (
    <button
      type="button"
      className={buttonClass}
      onClick={!isDisabled ? props.onClick : undefined}
      disabled={isDisabled}
      data-automation={props.dataAttribute}
      aria-label={props.ariaLabel}
      aria-pressed={props.ariaPressed}
      aria-expanded={props.ariaExpanded}
      aria-haspopup={props.ariaHasPopup}
      aria-busy={props.isLoading}
    >
      {props.isLoading ? (
        <span className={styles.spinner} />
      ) : (
        IconLead && <IconLead className={iconClass} />
      )}
      {props.text && (
        <span className={styles.textWrapper}>
          <Trans i18nKey={props.text} />
        </span>
      )}
      {IconTrail && <IconTrail className={iconClass} />}
      {props.children}
    </button>
  );
};
